/**
 * Money / number formatting utilities (UID-64, screens.md §16). The Orders
 * spend section renders server-derived decimal-string amounts (ADR-0029 —
 * an Order stores no total, so every money figure the BFF returns is already
 * a `{ amount: string, currency: string }` pair, e.g. `Money`/`SpendSummary`
 * in `packages/shared/src/order-summary.ts`). These helpers turn those wire
 * shapes into locale-formatted display strings; they never do the money MATH
 * themselves (that stays server-side) — only presentation.
 */

/** The `{ amount, currency }` wire shape every server-derived money figure uses. */
export interface MoneyLike {
  readonly amount: string
  readonly currency: string
}

const DEFAULT_LOCALE = 'en-US'

/**
 * Formats a `{ amount, currency }` pair as a localized currency string, e.g.
 * `{ amount: '1234.5', currency: 'USD' }` -> `"$1,234.50"`. Falls back to the
 * raw amount string (rather than throwing or showing "NaN") if the amount
 * can't be parsed as a finite number — a malformed server value should
 * degrade visibly, not crash the page.
 */
export function formatMoney(
  { amount, currency }: MoneyLike,
  locale: string = DEFAULT_LOCALE,
): string {
  const value = Number(amount)
  if (!Number.isFinite(value)) {
    return amount
  }
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)
}

/** Formats a plain count (order count, unit count) with locale grouping. */
export function formatNumber(value: number, locale: string = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Formats a percentage-change value with an explicit sign, e.g. `12.3` ->
 * `"+12.3%"`, `-4` -> `"-4%"`. Used for the spend %-change badge — the sign
 * is part of the TEXT on purpose (accessibility-wcag: never color alone), so
 * callers should still pair this with a tone/icon, not rely on the sign text
 * alone to carry the "improved vs. worsened" meaning for low-vision users.
 */
export function formatSignedPercent(value: number, locale: string = DEFAULT_LOCALE): string {
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    Math.abs(value),
  )
  const sign = value > 0 ? '+' : value < 0 ? '-' : '±'
  return `${sign}${formatted}%`
}
